/**
 * Sunscar Dunes: D Hijaz. A tanpura drone cycling Pa–Sa–Sa–Sa, a breathy reed pad, oud melody
 * with grace notes and slides (qanun with tremolo when calm), handpan arpeggios, Hijaz
 * tetrachord cadences, and — while running — doumbek grooves (maqsum / saidi) with riq jingles.
 */
import { FadeRoute } from '../bus';
import { dum, ka, riq, tek } from '../instruments/drums';
import { handpan, zill } from '../instruments/mallets';
import { padNote, tanpura } from '../instruments/pads';
import { oud, oudBass, qanun } from '../instruments/pluck';
import type { Synth } from '../instruments/synth';
import { degreeMidi, type ThemeDef } from '../themes';
import { disconnect, stopSource, type Rng } from '../util';
import type { BarInfo, Composer, MusicCore } from './composer';
import { Harmony, MelodyLine, MelodyWalker, type Chord } from './melody';
import { pats, type Pattern } from './patterns';

// Degrees: 0 D, 1 Eb, 2 F#, 3 G, 4 A, 5 Bb, 6 C
const CHORDS: readonly Chord[] = [
  { name: 'D', bass: 50, pad: [50, 54, 57], tones: [0, 2, 4], rest: [0, 4] },
  { name: 'Eb', bass: 51, pad: [51, 55, 58], tones: [1, 3, 5], rest: [1, 3] },
  { name: 'Cm', bass: 48, pad: [48, 55, 63], tones: [6, 1, 3], rest: [6, 3] },
  { name: 'Gm', bass: 43, pad: [50, 55, 58], tones: [3, 5, 0], rest: [3, 0] },
];

const PROGS: readonly (readonly number[])[] = [
  [0, 0, 1, 0],
  [0, 2, 1, 0],
  [3, 2, 1, 0],
  [0, 3, 1, 0],
  [0, 1, 2, 1],
];

// Indexed by percussion level 0..4. Maqsum: D T . T D . T .  Saidi: D T . D D . T .
const DUM_MAQSUM = pats(['', 'x.......x.......', 'X.......x.......', 'X.......x.......', 'X.......x.......']);
const TEK_MAQSUM = pats(['', '', '..x...x.....x...', '..x...x.....x...', '..x...x.....x...']);
const DUM_SAIDI = pats(['', 'x.......x.......', 'X.....x.x.......', 'X.....x.x.......', 'X.....x.x.......']);
const TEK_SAIDI = pats(['', '', '..x.........x...', '..x.........x...', '..x.........x...']);
const KA = pats(['', '', '', '....x.....x...x.', '.x..x....xx...x.']);
const RIQ = pats(['', '', '', '', 'x.x.X.x.x.x.X.x.']);

/** Tanpura cycle: Pa (A2) – Sa (D3) – Sa (D3) – low Sa (D2). */
const TANPURA = [45, 50, 50, 38];
/** Descending Hijaz tetrachord G F# Eb D. */
const CADENCE = [3, 2, 1, 0];

const BARS_PER_CHORD = 2;

export class DunesComposer implements Composer {
  readonly route: FadeRoute;
  private readonly s: Synth;
  private readonly pad: Synth;
  private readonly padIn: GainNode;
  private readonly padLp: BiquadFilterNode;
  private readonly padSend: GainNode;
  private readonly lfo: OscillatorNode;
  private readonly lfoAmt: GainNode;
  private readonly rng: Rng;
  private readonly harmony: Harmony;
  private readonly line: MelodyLine;
  private chord: Chord;
  private chordBar = 0;
  private phraseBar = 0;
  private saidi = false;
  private cadenceRun = false;
  private cadenceBase = 0;

  constructor(
    core: MusicCore,
    private readonly theme: ThemeDef,
    now: number,
  ) {
    const ctx = core.kit.ctx;
    this.route = new FadeRoute(ctx, core.bus, 0);
    this.s = { kit: core.kit, pool: core.pool, route: this.route };

    this.padIn = ctx.createGain();
    this.padLp = ctx.createBiquadFilter();
    this.padLp.type = 'lowpass';
    this.padLp.frequency.value = 750;
    this.padLp.Q.value = 0.7;
    this.padSend = ctx.createGain();
    this.padSend.gain.value = 0.45;
    this.padIn.connect(this.padLp);
    this.padLp.connect(this.route.dry);
    this.padLp.connect(this.padSend);
    this.padSend.connect(this.route.wet);
    this.lfo = ctx.createOscillator();
    this.lfo.frequency.value = 0.05;
    this.lfoAmt = ctx.createGain();
    this.lfoAmt.gain.value = 260;
    this.lfo.connect(this.lfoAmt);
    this.lfoAmt.connect(this.padLp.frequency);
    this.lfo.start(now);
    this.pad = { kit: core.kit, pool: core.pool, route: { dry: this.padIn, wet: this.padIn } };

    this.rng = core.kit.rng;
    this.harmony = new Harmony(CHORDS, PROGS, this.rng);
    // Melody: D4 … A5 (degrees 0..11), starting on D5.
    this.line = new MelodyLine(new MelodyWalker(theme.scale.length, 0, 11, this.rng, 7), this.rng);
    this.chord = CHORDS[0] as Chord;
  }

  bar(b: BarInfo): void {
    const rng = this.rng;
    if (b.restart) {
      this.harmony.restart();
      this.chordBar = 0;
      this.phraseBar = 0;
    }
    if (this.phraseBar === 0) {
      this.saidi = rng.chance(0.35);
      if (b.percLevel >= 3 && rng.chance(0.35)) zill(this.s, b.t, 0.35, { pan: rng.bi() * 0.5 });
    }
    if (this.chordBar === 0) {
      this.chord = this.harmony.next();
      const dur = b.barDur * BARS_PER_CHORD;
      const pad = this.chord.pad;
      for (let k = 0; k < pad.length; k++) {
        padNote(this.pad, b.t + k * 0.04, pad[k] as number, dur, 0.75, {
          attack: b.playing ? 1.2 : 2.2,
          release: 3,
          wave: this.s.kit.waves.reed,
          detune: 5,
          pan: (k - 1) * 0.25,
        });
      }
      this.padLp.frequency.setTargetAtTime(650 + 600 * b.intensity, b.t, 1.5);
      // Handpan arpeggio in the calm.
      if (b.percLevel <= 1 && rng.chance(0.6)) {
        const beat = b.barDur / 4;
        const offs = [0, 1, 2.5];
        for (let k = 0; k < offs.length; k++) {
          const m = (pad[k % pad.length] as number) + 12;
          handpan(this.s, b.t + (offs[k] as number) * beat, m, 0.34 - k * 0.04, { pan: (k - 1) * 0.3 });
        }
      }
    }
    this.chordBar = (this.chordBar + 1) % BARS_PER_CHORD;

    this.cadenceRun = this.phraseBar === 3 && rng.chance(0.5);
    if (this.cadenceRun) {
      this.cadenceBase = this.line.walker.deg >= 7 ? 7 : 0;
      this.line.silence();
    } else {
      this.line.bar(b.density, this.phraseBar, b.playing ? 0.1 : 0.45);
    }
    this.phraseBar = (this.phraseBar + 1) % 4;
  }

  private melodyNote(t: number, deg: number, vel: number, final: boolean, b: BarInfo): void {
    const rng = this.rng;
    const midi = degreeMidi(this.theme, deg, this.theme.melodyRoot);
    const pan = rng.bi() * 0.2;
    if (b.playing) {
      if (!final && rng.chance(0.18)) {
        // Grace note from the upper neighbour, then the note.
        const g = degreeMidi(this.theme, deg + 1, this.theme.melodyRoot);
        oud(this.s, t, g, vel * 0.45, { decay: 0.2, pan });
        oud(this.s, t + 0.055, midi, vel, { pan });
      } else {
        oud(this.s, t, midi, vel, { pan, slide: !final && rng.chance(0.12) ? 1 : 0 });
      }
    } else {
      qanun(this.s, t, midi, vel * 0.9, { pan });
      if (final || rng.chance(0.2)) {
        // Qanun tremolo: quick, fading re-plucks.
        for (let k = 1; k <= 3; k++) qanun(this.s, t + k * 0.07, midi, vel * (0.55 - k * 0.1), { pan, decay: 0.5 });
      }
    }
  }

  step(i: number, t: number, b: BarInfo): void {
    const rng = this.rng;
    const L = b.percLevel;

    // Drone.
    const every = L >= 3 ? 8 : 4;
    if (i % every === 0) {
      const idx = i / 4;
      const m = L >= 3 ? (i === 0 ? 45 : 50) : (TANPURA[idx] ?? 50);
      tanpura(this.s, t, m, 3.2, b.playing ? 0.65 : 0.8, idx * 0.15 - 0.22);
    }

    // Melody.
    if (this.cadenceRun) {
      if (i % 4 === 0) {
        const k = i / 4;
        const deg = this.cadenceBase + (CADENCE[k] ?? 0);
        const last = k === CADENCE.length - 1;
        this.melodyNote(t, deg, (b.playing ? 0.6 : 0.5) * (last ? 1 : 0.85), last, b);
        if (last) this.line.walker.deg = this.cadenceBase;
      }
    } else {
      const deg = this.line.step(i, this.chord);
      if (!Number.isNaN(deg)) {
        const vel = (b.playing ? 0.6 : 0.5) * (i % 8 === 0 ? 1 : 0.82) * rng.range(0.9, 1.05);
        this.melodyNote(t + rng.next() * 0.006, deg, vel, this.line.isFinal(i), b);
      }
    }

    // Percussion.
    if (L > 0 && L < DUM_MAQSUM.length) {
      const dums: readonly Pattern[] = this.saidi ? DUM_SAIDI : DUM_MAQSUM;
      const teks: readonly Pattern[] = this.saidi ? TEK_SAIDI : TEK_MAQSUM;
      const d = dums[L]?.[i] ?? 0;
      if (d) {
        dum(this.s, t, d === 2 ? 0.9 : 0.72);
        if (L >= 2) oudBass(this.s, t, this.chord.bass, 0.6, { decay: 0.6 });
      }
      if (teks[L]?.[i]) tek(this.s, t, 0.75, { pan: 0.18 });
      if (KA[L]?.[i]) ka(this.s, t, rng.range(0.5, 0.75), { pan: -0.2 });
      const r = RIQ[L]?.[i] ?? 0;
      if (r) riq(this.s, t, r === 2 ? 0.7 : 0.4, { pan: 0.35 });
    }
  }

  dispose(): void {
    stopSource(this.lfo);
    disconnect(this.lfo);
    disconnect(this.lfoAmt);
    disconnect(this.padIn);
    disconnect(this.padLp);
    disconnect(this.padSend);
    this.route.dispose();
  }
}
